import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, SelectField } from "@/components/common/Field";
import { DatePicker } from "@/components/common/DatePicker";
import { STATUS_ORDER, statusMeta } from "@/lib/constants";
import { todayISO } from "@/lib/format";
import { Loader2 } from "lucide-react";

const STATUS_OPTS = [
  { value: "none", label: "Keep current status" },
  ...STATUS_ORDER.map((s) => ({ value: s, label: statusMeta(s).label })),
];

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export const AddFollowUpDialog = ({ open, onOpenChange, leadId, onAdded }) => {
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState(nowTime());
  const [status, setStatus] = useState("none");
  const [reminder, setReminder] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setNote(""); setNextAction(""); setDate(todayISO()); setTime(nowTime());
    setStatus("none"); setReminder("");
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/followups`, {
        discussion_note: note || null,
        next_action: nextAction || null,
        follow_up_date: date || null,
        follow_up_time: time || null,
        status: status !== "none" ? status : null,
        reminder_date: reminder || null,
      });
      toast.success("Follow-up added");
      reset();
      onAdded?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add follow-up");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Add Follow-up</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Discussion note">
            <Textarea rows={3} autoFocus placeholder="What was discussed? e.g. Dealer considering ₹1.70L"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <Field label="Next action">
            <Input className="h-11" placeholder="e.g. Send revised offer" value={nextAction} onChange={(e) => setNextAction(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><DatePicker value={date} onChange={setDate} /></Field>
            <Field label="Time"><Input className="h-11" type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Update status"><SelectField value={status} onChange={setStatus} options={STATUS_OPTS} /></Field>
            <Field label="Next follow-up"><DatePicker value={reminder} onChange={setReminder} placeholder="Set reminder" /></Field>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button variant="brand" onClick={submit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Follow-up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
